import React, { useState } from 'react';

import { faCircleCheck, faCircleExclamation } from '@fortawesome/pro-regular-svg-icons';

import confirm from 'components/confirm';
import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { getStripe } from './client';
import { deleteRequest, post } from 'helpers/api';
import { showSnackbar } from 'components/ui/snackbar/helper';

import type { Customer } from './types';

interface Props {
  company: {
    name: string;
  },
  customer: Customer | null;
}

export function StripeCustomerSetup({ company, customer: initialCustomer }: Props) {
  const [customer, setCustomer] = useState<Customer | null>(initialCustomer);
  const [loading, setLoading] = useState(false);

  // Collects + verifies the brokerage's bank account entirely through Stripe.js, so the raw
  // account/routing numbers go browser -> Stripe and never touch our servers.
  async function handleConnectBank() {
    setLoading(true);

    try {
      const stripe = await getStripe();
      if (!stripe) {
        throw new Error('Stripe.js failed to load.');
      }

      const { clientSecret } = await post('/integrations/stripe/company_customer/create_setup_intent');

      const bankAccount = await stripe.collectBankAccountForSetup({
        clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              name: company.name,
            },
          },
        },
      });

      if (bankAccount.error) {
        throw new Error(bankAccount.error.message);
      }

      const confirmation = await stripe.confirmUsBankAccountSetup(clientSecret);
      if (confirmation.error) {
        throw new Error(confirmation.error.message);
      }

      const paymentMethod = confirmation.setupIntent?.payment_method;
      const paymentMethodId = typeof paymentMethod === 'string' ? paymentMethod : paymentMethod?.id;

      setCustomer(await post('/integrations/stripe/company_customer/attach_payment_method', { paymentMethodId }));
      showSnackbar({ text: 'Bank account connected.', variant: 'success' });
    } catch (e) {
      showSnackbar({ text: e instanceof Error ? e.message : 'Could not connect the bank account.', variant: 'danger' });
    } finally {
      setLoading(false);
    }
  }

  async function handleDisconnect() {
    try {
      await confirm('Disconnecting removes your funding bank account from Stripe.', {
        buttonLabel: 'Disconnect',
        buttonStyle: 'danger',
        title: 'Disconnect funding bank account',
      });
    } catch {
      return;
    }

    setLoading(true);

    try {
      await deleteRequest('/integrations/stripe/company_customer');
      setCustomer(null);
      showSnackbar({ text: 'Funding bank account disconnected.', variant: 'success' });
    } catch (e) {
      showSnackbar({
        text: typeof e === 'string' ? e : 'Could not disconnect the funding bank account.',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  }

  const configured = customer?.configured ?? false;
  const bankLabel = customer?.bankName ? `${customer.bankName} ••${customer.bankLast4 ?? ''}` : null;

  return (
    <div className="form-vertical-field-container">
      <p className="text-muted">
        Connect the bank account Stripe will debit to fund payouts to your brokers. Stripe collects
        and verifies the account details — Buildout never sees them.
      </p>

      {configured ? (
        <Alert icon={faCircleCheck} severity="success">
          Funding bank connected{bankLabel ? `: ${bankLabel}` : ''}.
        </Alert>
      ) : (
        <Alert icon={faCircleExclamation} severity="warning">
          No funding bank account is connected yet. Brokers can’t be paid via Stripe until one is.
        </Alert>
      )}

      <div className="d-flex gap-2">
        <Button disabled={loading} onClick={handleConnectBank}>
          {configured ? 'Change bank account' : 'Connect bank account'}
        </Button>
        {configured && (
          <Button disabled={loading} label="Disconnect" variant="destructive" onClick={handleDisconnect} />
        )}
      </div>
    </div>
  );
}
